import { _isNotNullish, _objectKeys } from '@naturalcycles/js-lib'
import {
  ActivationChannel,
  EMAIL_VERIFICATION_ON_WEB_QP,
  hwIdByProductKey,
  ProductKey,
  ProductType,
  ProductVariant,
  RegistrationFlow,
  shortQuizGoalOptions,
  WebSignupQueryParam,
} from '@naturalcycles/shared'
import { storageService } from '@src/app/core/services/storage.service.js'
import { PageFlowFnArgs } from '../services/flow.service.js'
// TODO figure out a way to fix this file. Some functions don't require both state and params, so
// having the FlowFnArgs type is not ideal.
export const flowUtils = {
  hasSession: ({ state }): boolean => !!state.sessionId,

  isLoggedIn: (args): boolean => flowUtils.hasAccountId(args) && flowUtils.hasSession(args),

  hasGoal: ({
    state: {
      account: { goal },
    },
  }): boolean => !!goal,

  hasPlan: ({ state }): boolean =>
    state.cart.items.some(item => item.type === ProductType.SUBSCRIPTION),

  hasMeasuringDevice: ({ state: { cart } }): boolean => {
    const measuringDevices = [ProductKey.OURA_RING, ..._objectKeys(hwIdByProductKey)]
    return cart.items.some(item => measuringDevices.includes(item.key))
  },

  hasNonSelectablePlan: ({ state: { product, cart } }): boolean => {
    const selectedPlan = cart.items.find(i => i.type === ProductType.SUBSCRIPTION)
    return !!selectedPlan && !product.items.some(i => i.key === selectedPlan.key)
  },

  hasAddress: ({
    state: {
      cart: { needsBillingAddress, needsShippingAddress },
      account: { shippingAddress, billingAddress },
    },
  }): boolean => {
    return !needsBillingAddress && !needsShippingAddress && (!!shippingAddress || !!billingAddress)
  },

  hasValidThermometerSelection: ({
    state: {
      cart: { items },
    },
  }): boolean => {
    const therm = items.find(item => item.key === ProductKey.THERMOMETER)
    return !therm || !!therm.variant
  },

  hasOrdered: ({
    state: {
      order: { transactionId },
    },
  }): boolean => !!transactionId,

  hasPaymentDue: ({
    state: {
      account: { nextPaymentDate },
    },
  }): boolean => {
    if (!nextPaymentDate) return false

    const now = new Date()
    const today = [
      now.getUTCFullYear(),
      (now.getUTCMonth() + 1).toString().padStart(2, '0'),
      now.getUTCDate().toString().padStart(2, '0'),
    ].join('-')

    return nextPaymentDate < today
  },

  hasCartItems: ({
    state: {
      cart: { items },
    },
  }): boolean => !!items.length,

  hasAccountId: ({
    state: {
      account: { id },
    },
  }): boolean => !!id,

  requiresAppConsent: ({ state, params }): boolean =>
    flowUtils.hasAccountId({ state, params }) && !state.account.appConsent,

  isOuraFlow: ({ params }): boolean => {
    return params?.[WebSignupQueryParam.flow] === RegistrationFlow.OURA.toString()
  },

  ownsOuraRing: ({
    state: {
      cart: { items },
    },
  }): boolean => {
    const ring = items.find(item => item.key === ProductKey.OURA_RING_DISCOUNT)
    return !!ring && ring.variant === ProductVariant.ownsDevice
  },

  isAppleWatchFlow: ({
    state: {
      cart: { items },
    },
  }): boolean => {
    // We don't use the discount tag for AW at the time of this refactor
    return items.some(item => item.key === ProductKey.APPLE_WATCH)
  },

  ownsAppleWatch: ({
    state: {
      cart: { items },
    },
  }): boolean => {
    const aw = items.find(item => item.key === ProductKey.APPLE_WATCH)
    return !!aw && aw.variant === ProductVariant.ownsDevice
  },

  hasCompletedQuiz: ({ state, params }): boolean => {
    const {
      quiz: { data },
    } = state
    const finishedNormalQuiz = !!data?.whichWearable
    const finishedShortQuiz =
      !!data?.fertilityGoal && shortQuizGoalOptions.includes(data.fertilityGoal)
    // We show a credentials modal on the last quiz page, so users should be logged in to be done
    const isLoggedIn = flowUtils.isLoggedIn({ state, params })
    // However, some goals skip that part of the quiz, so they'll see the credentials page later on
    return (finishedNormalQuiz && isLoggedIn) || finishedShortQuiz
  },

  hasCompletedCbQuiz: ({
    state: {
      quiz: { data },
    },
  }): boolean => {
    return (
      !!data?.cycleChanges ||
      (!!data?.fertilityGoal && shortQuizGoalOptions.includes(data.fertilityGoal))
    )
  },

  hasYearlySubscription: ({
    state: {
      cart: { items },
    },
  }): boolean => items.some(item => item.key === ProductKey.YEAR),

  hasCBCode: ({ state: { discountCode } }): boolean => {
    return discountCode?.activationChannel === ActivationChannel.CLEARBLUE
  },

  hasAcceptedCBOffer: ({ state, params }): boolean => {
    const { cart, order } = state
    const hasYearlyItem = (items: { key: string }[]): boolean =>
      items.some(item => item.key === ProductKey.YEAR)
    return (
      flowUtils.hasCBCode({
        state,
        params,
      }) &&
      (hasYearlyItem(cart.items) || hasYearlyItem(order.cart?.items || []))
    )
  },

  isEmailVerificationFlow: ({ params }): boolean => {
    return params?.[WebSignupQueryParam.flow] === EMAIL_VERIFICATION_ON_WEB_QP
  },

  // works to determine whether the user chose to continue with recommended device or view the devices page
  shouldSkipMeasuringDevicePage: (): boolean => {
    return storageService.get('skipMDP') === 'true'
  },

  hasSeenCoveredByInsurance: ({ params }): boolean => {
    return params['seenCBI'] === 'true'
  },

  hasDecidedOnCbOffer: ({ params }): boolean => {
    return _isNotNullish(params[WebSignupQueryParam.cbo])
  },

  hasInsuranceProviderParam: ({ params }): boolean => {
    return _isNotNullish(params[WebSignupQueryParam.ip])
  },

  hasInsuranceProviderVariantParam: ({ params }): boolean => {
    return _isNotNullish(params[WebSignupQueryParam.ipv])
  },

  requiresAddress: ({ state: { cart } }): boolean => {
    return !!(cart.requiresBillingAddress || cart.requiresShippingAddress)
  },

  isMetaLandingPageFlow: ({ params }): boolean => {
    return params[WebSignupQueryParam.flow] === RegistrationFlow.META_LANDING_PAGE.toString()
  },
} satisfies Record<string, (args: PageFlowFnArgs) => boolean>
